import { InviteLinksGroupChannelsType } from '@/shared/api'
import { useMemo } from 'react'

export interface GroupsChannelFormattedRowType {
    id: number
    groupName: string
    linksCount: number
    budget: number
    creationDate: string
    subscribers: number
    reach: number
    subscriptions: number
    cpm: number
    cps: number
    unsubscriptions: number
    unsubscriptionsPercentage: number
    total_cps: number
}

export const useGroupsChannelsFormattedRows = (
    groups: InviteLinksGroupChannelsType[]
): GroupsChannelFormattedRowType[] => {
    const formattedRows = useMemo(() => {
        let totalLinksCount = 0
        const rows = groups.map((group) => {
            const links = group.inviteLinks
            const links_group_id = group.invite_links_group_id

            const linksCount = links.length

            const budget = links.reduce(
                (sum, link) => sum + (link.budget || 0),
                0
            )

            totalLinksCount += group.inviteLinks.length

            const creationDate = group.created_at
            const subscribers = links.reduce(
                (sum, link) =>
                    sum + (link?.publisher?.telegram_channel_subscribers || 0),
                0
            )
            const reach = links.reduce(
                (sum, link) => sum + (link.publisher?.telegram_post_reach || 0),
                0
            )
            const subscriptions = links.reduce(
                (sum, link) => sum + (link.total_subscribers || 0),
                0
            )
            const totalCost = budget
            const totalImpressions = reach
            const cpm = totalImpressions > 0 ? totalCost / totalImpressions : 0
            const cps = subscriptions > 0 ? totalCost / subscriptions : 0
            const unsubscriptions = links.reduce(
                (sum, link) => sum + (link.unsubscribed_count || 0),
                0
            )
            const unsubscriptionsPercentage =
                subscriptions > 0 ? (unsubscriptions / subscriptions) * 100 : 0

            const retained = subscriptions - unsubscriptions
            const total_cps = budget && retained ? budget / retained : 0

            return {
                id: links_group_id,
                groupName: group.group_name,
                linksCount,
                budget,
                creationDate,
                subscribers,
                reach,
                subscriptions,
                cpm,
                cps,
                unsubscriptions,
                unsubscriptionsPercentage,
                total_cps,
            }
        })
        const allLinksGroupDb = rows.find(
            (row) => row.groupName === 'Все ссылки'
        )
        if (allLinksGroupDb) {
            allLinksGroupDb.linksCount = totalLinksCount
        }
        const filteredRows = rows.filter(
            (row) => row.groupName !== 'Все ссылки'
        )

        const aggregate = filteredRows.reduce(
            (acc, row) => {
                acc.linksCount += row.linksCount
                acc.budget += row.budget
                acc.subscribers += row.subscribers
                acc.reach += row.reach
                acc.subscriptions += row.subscriptions
                acc.unsubscriptions += row.unsubscriptions
                acc.total_cps += row.total_cps
                return acc
            },
            {
                linksCount: 0,
                budget: 0,
                subscribers: 0,
                reach: 0,
                subscriptions: 0,
                unsubscriptions: 0,
                total_cps: 0,
            }
        )
        const totalBudget = allLinksGroupDb?.budget
            ? allLinksGroupDb?.budget + aggregate.budget
            : aggregate.budget
        const totalReach = allLinksGroupDb?.reach
            ? allLinksGroupDb?.reach + aggregate.reach
            : aggregate.reach
        const totalSubscribers = allLinksGroupDb?.subscribers
            ? allLinksGroupDb?.subscribers + aggregate.subscribers
            : aggregate.subscribers
        const totalSubscriptions = allLinksGroupDb?.subscriptions
            ? allLinksGroupDb?.subscriptions + aggregate.subscriptions
            : aggregate.subscriptions
        const totalUnsubscriptions = allLinksGroupDb?.unsubscriptions
            ? allLinksGroupDb?.unsubscriptions + aggregate.unsubscriptions
            : aggregate.unsubscriptions
        const cpm = totalReach > 0 ? totalBudget / totalReach : 0
        const cps =
            totalSubscriptions > 0 ? totalBudget / totalSubscriptions : 0
        const unsubscriptionsPercentage =
            totalSubscriptions > 0
                ? (totalUnsubscriptions / totalSubscriptions) * 100
                : 0
        const totalCps = allLinksGroupDb?.total_cps
            ? allLinksGroupDb?.total_cps + aggregate.total_cps
            : aggregate.total_cps
        const allLinksGroup: GroupsChannelFormattedRowType = {
            id: allLinksGroupDb?.id || 0,
            groupName: 'Все ссылки',
            linksCount: allLinksGroupDb?.linksCount ?? 0, // aggregate.linksCount,
            budget: totalBudget,
            creationDate: allLinksGroupDb?.creationDate || '',
            subscribers: totalSubscribers,
            reach: totalReach,
            subscriptions: totalSubscriptions,
            cpm,
            cps,
            unsubscriptions: totalUnsubscriptions,
            unsubscriptionsPercentage,
            total_cps: totalCps,
        }
        return [...(rows.length ? [allLinksGroup] : []), ...filteredRows]
    }, [groups])

    return formattedRows
}
